import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Button from '@/components/base/Button';
import EmptyState from '@/components/base/EmptyState';
import Toast, { type ToastType } from '@/components/base/Toast';
import DriversTable from '@/pages/drivers/components/DriversTable';
import { useAppData } from '@/store/AppDataContext';

interface ToastState { type: ToastType; message: string }

export default function DriversPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { drivers, toggleDriverStatus } = useAppData();

  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const showToast = (type: ToastType, message: string) => setToast({ type, message });

  const handleToggleStatus = (driverId: string) => {
    const driver = drivers.find((d) => d.id === driverId);
    if (!driver) return;
    toggleDriverStatus(driverId);
    showToast(
      'info',
      driver.status === 'disabled' ? `تم تفعيل ${driver.name}` : `تم تعطيل ${driver.name}`
    );
  };

  const availableCount = drivers.filter((d) => d.status === 'available').length;

  return (
    <DashboardLayout title="المندوبين التابعين">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-[18px] text-foreground-950">المندوبين التابعين</h2>
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary-100 px-2 text-[12px] font-semibold text-primary-700">
              {drivers.length}
            </span>
          </div>
          <Button variant="cta" size="md" icon="ri-user-add-line" onClick={() => navigate('/drivers/new')}>
            أضف مندوب جديد
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: 'إجمالي المندوبين', value: drivers.length, icon: 'ri-e-bike-2-line', tone: 'secondary' },
            { label: 'متاحون الآن', value: availableCount, icon: 'ri-user-follow-line', tone: 'success' },
            { label: 'المشغولون', value: drivers.filter((d) => d.status === 'busy').length, icon: 'ri-loader-2-line', tone: 'warning' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 rounded-card border border-background-200 bg-white p-4 shadow-card"
            >
              <span className={['flex h-10 w-10 items-center justify-center rounded-[10px]',
                stat.tone === 'secondary' ? 'bg-secondary-100 text-secondary-600' :
                stat.tone === 'success' ? 'bg-success-100 text-success-600' :
                'bg-warning-100 text-warning-600',
              ].join(' ')}>
                <i className={`${stat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-[12px] text-foreground-500">{stat.label}</p>
                <p className="font-bold text-[24px] text-foreground-950">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        {drivers.length === 0 ? (
          <EmptyState
            icon="ri-e-bike-2-line"
            title="لم تضف أي مندوب بعد"
            message="أضف مندوبيك التابعين وهم سيتلقون المهام مباشرةً على تطبيق السائق."
            action={
              <Button variant="cta" icon="ri-user-add-line" onClick={() => navigate('/drivers/new')}>
                أضف أول مندوب
              </Button>
            }
          />
        ) : (
          <DriversTable
            drivers={drivers}
            onView={(driver) => navigate(`/drivers/${driver.id}`)}
            onEdit={(driver) => navigate(`/drivers/${driver.id}/edit`)}
            onResetPassword={(driver) => navigate(`/drivers/${driver.id}/reset-password`)}
            onToggleStatus={(driver) => handleToggleStatus(driver.id)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}